/**
 * Helpers para impressão de etiquetas ZPL em Zebra ZD220.
 *
 * Estratégia "Raw Print" no Windows:
 * 1. Instalar a impressora ZD220 como "Generic / Text Only" no Painel de Controle,
 *    apontando para a porta USB (USB001) ou IP (RAW 9100) do equipamento.
 * 2. Compartilhar a impressora (opcional, para imprimir de outras estações).
 * 3. Frontend abre o ZPL como text/plain numa janela e dispara window.print();
 *    o driver "Generic / Text Only" envia os bytes brutos direto para a Zebra,
 *    que os interpreta como ZPL II.
 *
 * Para etiquetas 50x30 mm a impressora deve estar calibrada (botão FEED ~3s)
 * e configurada para mídia "Contínua" no Zebra Setup Utilities.
 */

import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import { getToken } from '@/shared/api/client';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';
const PRINT_MODE_KEY = 'ictus.print.mode';

export type PrintMode = 'zebra' | 'common';

interface LabelPreview {
  title: string;
  subtitle?: string;
  barcode: string;
  qr_code: string;
  lines: string[];
  zpl: string;
}

async function fetchZpl(path: string): Promise<string> {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { Authorization: `Bearer ${token ?? ''}` },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Falha ao gerar etiqueta (${res.status}): ${text || res.statusText}`);
  }
  return res.text();
}

async function fetchPreview(path: string): Promise<LabelPreview> {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { Authorization: `Bearer ${token ?? ''}` },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Falha ao montar pré-visualização (${res.status}): ${text || res.statusText}`);
  }
  return res.json() as Promise<LabelPreview>;
}

function escapeHtml(v: string): string {
  return v.replace(/[&<>]/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[m]!));
}

function repeatedZpl(zpl: string, copies: number): string {
  return Array.from({ length: Math.max(1, copies) }, () => zpl).join('\n');
}

async function makeBarcodeDataUrl(barcode: string): Promise<string> {
  const canvas = document.createElement('canvas');
  JsBarcode(canvas, barcode, {
    format: 'CODE128',
    displayValue: true,
    fontSize: 14,
    margin: 4,
    height: 64,
    width: 1.6,
  });
  return canvas.toDataURL('image/png');
}

async function makeQrDataUrl(data: string): Promise<string> {
  return QRCode.toDataURL(data, {
    width: 140,
    margin: 1,
    errorCorrectionLevel: 'M',
  });
}

async function openVisualPrintWindow(preview: LabelPreview, mode: PrintMode, copies = 1): Promise<void> {
  const win = window.open('', 'IctusLabelPreview', 'width=760,height=620');
  if (!win) throw new Error('Bloqueador de pop-up impede a impressão. Permita pop-ups para este site.');

  const barcodeDataUrl = await makeBarcodeDataUrl(preview.barcode);
  const qrDataUrl = await makeQrDataUrl(preview.qr_code || preview.barcode);
  const zpl = repeatedZpl(preview.zpl, copies);

  win.document.open();
  win.document.write(`<!doctype html>
<html>
<head>
  <title>Pré-visualização da etiqueta</title>
  <style>
    :root { color-scheme: light; }
    body { margin: 0; font-family: Segoe UI, Arial, sans-serif; background: #f3f6fa; color: #1f2937; }
    .layout { padding: 16px; display: grid; gap: 12px; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .btn { border: 1px solid #9ca3af; border-radius: 6px; background: #fff; color: #111827; padding: 8px 12px; cursor: pointer; font-weight: 600; }
    .btn.primary { background: #0078d4; border-color: #0078d4; color: #fff; }
    .hint { font-size: 12px; color: #4b5563; }
    .label { background: #fff; border: 1px solid #d1d5db; border-radius: 6px; width: 50mm; min-height: 30mm; padding: 3mm; box-sizing: border-box; }
    .title { font-size: 12px; font-weight: 700; margin-bottom: 2px; }
    .sub { font-size: 10px; color: #4b5563; margin-bottom: 4px; }
    .content { display: grid; grid-template-columns: 1fr 24mm; gap: 3mm; align-items: start; }
    .qr { width: 24mm; height: 24mm; object-fit: contain; }
    .bar { width: 100%; max-height: 16mm; object-fit: contain; }
    .meta { margin-top: 1mm; font-size: 9px; line-height: 1.3; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .mono { font-family: Consolas, monospace; font-size: 10px; }
    pre { max-height: 140px; overflow: auto; background: #0b1220; color: #e5e7eb; padding: 10px; border-radius: 6px; font-size: 11px; }
    @page { size: 50mm 30mm; margin: 0; }
    @media print {
      body { background: #fff; }
      .actions, .hint, .zplbox { display: none !important; }
      .layout { padding: 0; }
      .label { border: 0; border-radius: 0; }
    }
  </style>
</head>
<body>
  <div class="layout">
    <div class="actions">
      <button class="btn ${mode === 'common' ? 'primary' : ''}" onclick="printCommon()">Imprimir em impressora comum</button>
      <button class="btn ${mode === 'zebra' ? 'primary' : ''}" onclick="printZebra()">Imprimir em Zebra (ZPL)</button>
      <button class="btn" onclick="window.close()">Fechar</button>
    </div>
    <div class="hint">Pré-visualização obrigatória antes de imprimir. Revise QR, barcode e dados do rótulo.</div>
    <div class="label" id="label-root">
      <div class="title">${escapeHtml(preview.title)}</div>
      ${preview.subtitle ? `<div class="sub">${escapeHtml(preview.subtitle)}</div>` : ''}
      <div class="content">
        <div>
          <img class="bar" src="${barcodeDataUrl}" alt="Barcode" />
          <div class="mono">${escapeHtml(preview.barcode)}</div>
          ${preview.lines.map((ln) => `<div class="meta">${escapeHtml(ln)}</div>`).join('')}
        </div>
        <img class="qr" src="${qrDataUrl}" alt="QR" />
      </div>
    </div>
    <div class="zplbox">
      <div class="hint">ZPL bruto (modo Zebra)</div>
      <pre>${escapeHtml(zpl)}</pre>
    </div>
  </div>
  <script>
    const repeatedZpl = ${JSON.stringify(zpl)};
    function printCommon() {
      window.print();
    }
    function printZebra() {
      document.body.innerHTML = '<pre style="margin:0;padding:0;font-family:monospace;white-space:pre">' + repeatedZpl
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') + '</pre>';
      setTimeout(() => window.print(), 150);
      window.addEventListener('afterprint', () => window.close(), { once: true });
    }
  </script>
</body>
</html>`);
  win.document.close();
}

export function getPreferredPrintMode(): PrintMode {
  try {
    const mode = localStorage.getItem(PRINT_MODE_KEY);
    return mode === 'common' ? 'common' : 'zebra';
  } catch {
    return 'zebra';
  }
}

export function setPreferredPrintMode(mode: PrintMode) {
  try { localStorage.setItem(PRINT_MODE_KEY, mode); } catch { /* no-op */ }
}

/**
 * Abre uma janela de impressão com o ZPL bruto pré-formatado.
 * Requer que a impressora padrão seja a Zebra "Generic / Text Only".
 */
export function openPrintWindow(zpl: string, copies = 1): void {
  const win = window.open('', 'ZebraPrint', 'width=400,height=300');
  if (!win) {
    throw new Error('Bloqueador de pop-up impede a impressão. Permita pop-ups para este site.');
  }
  const repeated = Array.from({ length: Math.max(1, copies) }, () => zpl).join('\n');
  // <pre> com white-space:pre preserva CRLF/whitespace que o driver Text Only repassa.
  win.document.open();
  win.document.write(`<!doctype html>
<html><head><title>ICTUS Etiqueta</title>
<style>
@page { size: 50mm 30mm; margin: 0; }
body, pre { margin:0; padding:0; font-family: monospace; font-size: 8pt; white-space: pre; }
</style></head>
<body><pre>${repeated.replace(/[&<>]/g, (m) => ({ '&':'&amp;','<':'&lt;','>':'&gt;' }[m]!))}</pre>
<script>
  window.addEventListener('load', () => { setTimeout(() => { window.print(); }, 200); });
  window.addEventListener('afterprint', () => { window.close(); });
</script>
</body></html>`);
  win.document.close();
}

export async function printKitLabel(kitId: string, copies = 1) {
  const preview = await fetchPreview(`/v1/labels/kit/${kitId}/preview`);
  await openVisualPrintWindow(preview, getPreferredPrintMode(), copies);
}

export async function printItemLabel(itemId: string, copies = 1) {
  const preview = await fetchPreview(`/v1/labels/item/${itemId}/preview`);
  await openVisualPrintWindow(preview, getPreferredPrintMode(), copies);
}

export async function printBatchLabel(batchId: string, copies = 1) {
  const preview = await fetchPreview(`/v1/labels/batch/${batchId}/preview`);
  await openVisualPrintWindow(preview, getPreferredPrintMode(), copies);
}

export async function printTestLabel() {
  const preview = await fetchPreview('/v1/labels/test-print/preview');
  await openVisualPrintWindow(preview, getPreferredPrintMode(), 1);
}

export async function printRawZplForZebra(path: string, copies = 1) {
  const zpl = await fetchZpl(path);
  openPrintWindow(zpl, copies);
}
